import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { api } from '../api/client';
import { Plus, ChevronDown, ChevronUp, HardHat, Glasses, Hand, Footprints, Shield, Trash2, X, AlertTriangle } from 'lucide-react';
import SignatureCapture from '../components/shared/SignatureCapture';
import { formatInstantDay } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { useHideAgentFab } from '../components/AgentFab';
import { useMyWork } from '../hooks/useMyWork';
import { linkableItems, workItemLabel, workItemValue } from '../lib/work-item-selection';
import { unwrapArray } from '../lib/api-envelope';
import { buildJsaUpdate, JSA_SIGNED_PART_LABELS, jsaSignatureKey, jsaToFormState } from '../lib/jsa';
import { useOptionalAuth } from '../contexts/AuthContext';

/**
 * What the reader asked for. `save` keeps the JSA where it is (a new one is a
 * draft; an edited rejected one stays rejected); `submit` saves and then sends
 * it for approval. Before, "Submit for Approval" only ever created a draft and
 * "Save Draft" was the cancel handler.
 */
export type JsaSaveIntent = 'save' | 'submit';

interface JSAFormProps {
  onSubmit: (data: any, intent?: JsaSaveIntent) => void;
  onCancel: () => void;
  /**
   * Edit mode: the JSA as GET /safety/jsa/:id returned it. The form starts
   * from its title, steps, supervisor and signatures instead of the defaults.
   */
  existing?: any;
  /** A save is in flight; the actions are disabled. */
  saving?: boolean;
}

const DEFAULT_STEPS = [
  {
    title: 'RIG UP EQUIPMENT',
    hazards: ['Pinch points during setup', 'Heavy lifting (power tongs)', 'Slippery surfaces'],
    controls: ['Use proper lifting techniques', '2-person lift for heavy items', 'Apply sand/salt to icy areas'],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hi-vis vest'],
  },
  {
    title: 'THREAD WASH OPERATION',
    hazards: ['High pressure fluid injection', 'Chemical exposure', 'Noise exposure'],
    controls: ['Verify pressure relief valves functional', 'Wear chemical-resistant gloves', 'Use hearing protection'],
    ppe: ['Hard hat', 'Safety glasses', 'Chemical gloves', 'Hearing protection'],
  },
  {
    title: 'POWER TONGS OPERATION',
    hazards: ['Rotating equipment', 'Pinch points', 'High torque forces'],
    controls: ['Keep hands clear of dies', 'Use tongs only for intended purpose', 'Monitor torque gauge continuously'],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots'],
  },
];

type DefaultStep = { title: string; hazards: string[]; controls: string[]; ppe: string[] };

/*
 * The starting steps for a NEW JSA, by vertical. DEFAULT_STEPS above is the
 * casing set and stays exactly as it was: a casing tenant, alone or with other
 * verticals, still starts from it. A trucking or rentals tenant without casing
 * starts from its own work instead of thread wash and power tongs. Rentals
 * covers trailer hookup too, so a tenant with both gets the rentals set. Any
 * other tenant keeps the casing set, as before. Every PPE entry is one of
 * PPE_OPTIONS, so each renders as a toggle. These are starting points: every
 * step is editable, and a tenant's own templates replace them.
 */
const TRUCKING_STEPS: DefaultStep[] = [
  {
    title: 'PRE-TRIP AND TRAILER HOOKUP',
    hazards: ['Trailer rolling while coupling', 'Pinch points at the fifth wheel and air lines', 'Slips and falls getting on and off the deck'],
    controls: ['Chock wheels and set brakes before coupling', 'Tug test and check the fifth wheel is locked', 'Three points of contact on steps and deck'],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hi-vis vest'],
  },
  {
    title: 'LOADING, LIFTING AND SECUREMENT',
    hazards: ['Suspended loads', 'Load shifting in transit', 'Strain from chains and binders'],
    controls: ['Stay out from under suspended loads and use tag lines', 'Secure the load and recheck it after the first few kilometres and at stops', 'Use proper binder technique, with no cheater bars'],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hi-vis vest'],
  },
  {
    title: 'WELLSITE ARRIVAL AND HAZARDS',
    hazards: ['H2S or other gas release', 'Backing in a congested lease', 'Overhead power lines'],
    controls: ['Check in with the site supervisor and review the site hazard assessment', 'Use a spotter when backing', 'Keep clear of overhead lines when raising a deck or boom'],
    ppe: ['Hard hat', 'Safety glasses', 'Steel-toed boots', 'Hi-vis vest'],
  },
];

const RENTALS_STEPS: DefaultStep[] = [
  {
    title: 'TRAILER HOOKUP AND TRANSPORT',
    hazards: ['Trailer rolling while coupling', 'Pinch points at the hitch and safety chains', 'Load shifting in transit'],
    controls: ['Chock wheels and set brakes before coupling', 'Check the hitch, chains and lights before moving', 'Secure the equipment and recheck it at stops'],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hi-vis vest'],
  },
  {
    title: 'RIG-UP OF RENTAL EQUIPMENT',
    hazards: ['Stored pressure in lines and tanks', 'Pinch points connecting hoses and fittings', 'Trip hazards from hoses and cables'],
    controls: ['Confirm lines are depressurized before connecting', 'Use whip checks on pressurized connections', 'Route hoses and cables clear of walkways'],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hi-vis vest'],
  },
  {
    title: 'LIFTING AND PLACEMENT',
    hazards: ['Suspended loads', 'Equipment tipping on soft ground', 'Crushing between equipment'],
    controls: ['Inspect rigging and confirm the lifting certification is in date', 'Use tag lines and stay out of the line of fire', 'Check ground conditions and use rig mats where needed'],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hi-vis vest'],
  },
  {
    title: 'WELLSITE HAZARDS',
    hazards: ['H2S or other gas release', 'Overhead power lines', 'Vehicles and equipment moving on the lease'],
    controls: ['Check in with the site supervisor and review the site hazard assessment', 'Keep a safe distance from overhead lines', 'Use a spotter and follow the site traffic plan'],
    ppe: ['Hard hat', 'Safety glasses', 'Steel-toed boots', 'Hi-vis vest'],
  },
];

/*
 * Pressure testing: pre-job and rig-up, fill / low-pressure check / pressure-up,
 * the hold, and bleed-down. Operator-approved wording, 2026-10-09; editable
 * like every set. Hazards and controls are PAIRED BY POSITION — controls[n] is
 * the control for hazards[n] — so the two lists stay the same length (pinned by
 * JSAForm.vertical-defaults.test.tsx).
 */
const PRESSURE_TESTING_STEPS: DefaultStep[] = [
  {
    title: 'PRE-JOB AND RIG UP',
    hazards: [
      'Pinch points connecting lines and fittings',
      'Wrong-rated equipment',
      'Uncontrolled lines if a connection fails',
      'Trip hazards from hoses',
      'Heavy lifts (pump, test equipment)',
    ],
    controls: [
      'Hands clear; use the right tools; no hand on a moving connection',
      'Check hoses, fittings and valves are rated above the test pressure; confirm gauge/recorder calibration is current',
      'Whip checks on every pressurized connection',
      'Route lines clear of walkways',
      '2-person lift or mechanical aid',
    ],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hi-vis vest'],
  },
  {
    title: 'FILL, LOW-PRESSURE CHECK AND PRESSURE UP',
    hazards: [
      'Trapped air (stored energy)',
      'Leak or failure at full pressure',
      'Over-pressuring',
      'Miscommunication',
      'Glycol / methanol (winter)',
    ],
    controls: [
      'Fill and purge air from the system before pressuring up',
      'Hold a low-pressure check first; inspect for leaks before going to test pressure',
      'Never exceed the test pressure or the lowest-rated component; set the pump relief',
      'One person controls the pump; agreed signals or radio with the rig',
      'Review the SDS; chemical gloves; no ignition sources near methanol',
    ],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hearing protection'],
  },
  {
    title: 'HOLD',
    hazards: [
      'Hose or fitting failure under pressure',
      'People entering the test area',
      'Line of fire',
    ],
    controls: [
      'Exclusion zone set and flagged; larger zone for pneumatic tests',
      'Barricade and signage; only essential personnel',
      'Stay out of line with connections and plugs; never tighten or adjust under pressure',
    ],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hearing protection'],
  },
  {
    title: 'BLEED DOWN AND RIG OUT',
    hazards: [
      'Trapped pressure',
      'Release of test fluid',
      'Spills',
      'Pinch points breaking connections',
    ],
    controls: [
      'Bleed to zero and confirm on the gauge before breaking any connection',
      'Open bleed valves slowly, pointed away from people',
      'Drain and contain test fluid and any glycol/methanol per site rules',
      'Hands clear; right tools',
    ],
    ppe: ['Hard hat', 'Safety glasses', 'Chemical gloves', 'Steel-toed boots', 'Hi-vis vest'],
  },
];

/*
 * A tenant matching none of the sets above (downhole-only, say) starts from
 * work every wellsite job has, rather than casing's thread wash and tongs.
 */
const GENERIC_STEPS: DefaultStep[] = [
  {
    title: 'SITE ARRIVAL AND HAZARD ASSESSMENT',
    hazards: ['H2S or other gas release', 'Overhead power lines', 'Vehicles and equipment moving on the lease'],
    controls: ['Check in with the site supervisor and review the site hazard assessment', 'Keep a safe distance from overhead lines', 'Use a spotter and follow the site traffic plan'],
    ppe: ['Hard hat', 'Safety glasses', 'Steel-toed boots', 'Hi-vis vest'],
  },
  {
    title: 'EQUIPMENT HANDLING',
    hazards: ['Pinch points', 'Heavy lifting', 'Slippery surfaces'],
    controls: ['Keep hands clear of pinch points', 'Use proper lifting techniques and a 2-person lift for heavy items', 'Apply sand/salt to icy areas'],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots', 'Hi-vis vest'],
  },
  {
    title: 'HOUSEKEEPING AND RIG OUT',
    hazards: ['Trip hazards from tools and cables', 'Stored energy in equipment', 'Strain from lifting and carrying'],
    controls: ['Keep walkways clear and store tools as you go', 'Isolate and release stored energy before disconnecting', 'Use proper lifting techniques'],
    ppe: ['Hard hat', 'Safety glasses', 'Work gloves', 'Steel-toed boots'],
  },
];

/**
 * Positive guards only: casing wins whenever it is present, and no session
 * keeps the casing set as before. A tenant with none of the named verticals
 * gets the generic set, never casing's.
 */
export function defaultJsaSteps(hasVertical?: (v: string) => boolean): DefaultStep[] {
  if (!hasVertical || hasVertical('casing_running')) return DEFAULT_STEPS;
  if (hasVertical('surface_rentals')) return RENTALS_STEPS;
  if (hasVertical('trucking')) return TRUCKING_STEPS;
  if (hasVertical('pressure_testing')) return PRESSURE_TESTING_STEPS;
  return GENERIC_STEPS;
}

const ppeIcons: Record<string, React.ReactNode> = {
  'Hard hat': <HardHat size={16} />,
  'Safety glasses': <Glasses size={16} />,
  'Work gloves': <Hand size={16} />,
  'Chemical gloves': <Hand size={16} />,
  'Steel-toed boots': <Footprints size={16} />,
  'Hi-vis vest': <Shield size={16} />,
  'Hearing protection': <HardHat size={16} />,
};
const PPE_OPTIONS = Object.keys(ppeIcons);

type Step = { id: number; title: string; hazards: string[]; controls: string[]; ppe: string[] };

const inputClass = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white';

/** The job an existing JSA belongs to, read-only: PUT ignores jobId. */
function jobLabel(jsa: any): string {
  const number = jsa?.job_number ?? jsa?.jobNumber;
  const well = jsa?.well_name ?? jsa?.wellName;
  if (!(jsa?.job_id ?? jsa?.jobId)) return 'General JSA (no specific job)';
  return [number, well].filter(Boolean).join(' - ') || '—';
}

/**
 * One editable list — a step's hazards or its controls. A rejection is usually
 * about exactly these, so every entry is an input, removable, and the list
 * grows. Blank entries are dropped when the JSA is saved.
 */
function ListEditor({ label, items, onChange, addLabel }: {
  label: string; items: string[]; onChange: (next: string[]) => void; addLabel: string;
}) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-2">
          <input
            value={item}
            onChange={(e) => onChange(items.map((v, j) => (j === i ? e.target.value : v)))}
            aria-label={`${label} ${i + 1}`}
            className={inputClass}
          />
          <button
            type="button"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            aria-label={`Remove ${label.toLowerCase()} ${i + 1}`}
            className="p-1 text-fieldiq-muted hover:text-fieldiq-text"
          >
            <X size={16} />
          </button>
        </li>
      ))}
      <li>
        <button
          type="button"
          onClick={() => onChange([...items, ''])}
          className="inline-flex items-center gap-1 text-xs font-medium text-fieldiq-text hover:underline"
        >
          <Plus size={14} /> {addLabel}
        </button>
      </li>
    </ul>
  );
}

export default function JSAForm({ onSubmit, existing, saving = false }: JSAFormProps) {
  useHideAgentFab();  // bottom action bar — the FAB would cover Submit.
  const timeZone = useTenantTimeZone();
  const auth = useOptionalAuth();
  const isEdit = !!existing;
  const submissionId = useMemo(() => (existing?.id as string | undefined) ?? uuidv4(), [existing?.id]);
  const [expandedSteps, setExpandedSteps] = useState<number[]>([0]);

  // Positive guards. No auth context at all (a few embedded uses) keeps the
  // historic casing behaviour, the same rule defaultJsaSteps follows.
  const hasCasing = !auth || auth.hasVertical('casing_running');
  const hasPt = !!auth?.hasVertical('pressure_testing');

  // Load real jobs (create only — an existing JSA's job cannot be changed).
  // GET /jobs is casing-gated: without casing it 403'd into an empty picker.
  const { data: jobsData } = useQuery({
    queryKey: ['jobs-for-jsa'],
    queryFn: () => api.jobs.list().then(r => unwrapArray<any>(r.data)),
    enabled: !isEdit && hasCasing,
  });
  // The hand's own open PT jobs, from GET /field/my-work. Already open-only.
  const { data: myWork } = useMyWork('open', { enabled: !isEdit && hasPt });
  const ptItems = linkableItems(myWork?.items, 'pressure_testing');
  /* A JSA is written BEFORE the work happens, so a finished job is never the
   * right answer. /jobs returns every status — on Smith Casing that is 12 of
   * 24 rows — and burying the ten schedulable jobs among them is how the wrong
   * job gets picked. Filtered here, client-side, deliberately: the endpoint is
   * shared with screens that DO want the full history.
   *
   * DELIBERATELY UNLIKE Report Incident, which shows every status. That form
   * records something that already happened and may well have happened on a
   * job that has since completed. The two pickers differ because the two forms
   * point in opposite directions in time — this is not an oversight to
   * reconcile. See the matching note in IncidentReportForm. */
  const jobs: any[] = (jobsData ?? []).filter((j: any) => {
    const status = (j.status || j.job_status || '').toLowerCase();
    return status !== 'completed' && status !== 'cancelled' && status !== 'canceled';
  });

  /* Company-scoped JSA templates. Replaces three hardcoded vertical names that
   * were the same for every tenant and matched no record on either side. */
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['jsa-templates'],
    queryFn: () => api.safety.jsaTemplates().then(r => unwrapArray<any>(r.data)),
    enabled: !isEdit,
  });
  const templates: any[] = templatesData ?? [];
  const templateName = (t: any) => t.name || t.title || t.template_name || t.templateName || 'Untitled template';

  // Load real team members
  const { data: usersData } = useQuery({
    queryKey: ['users-for-jsa'],
    queryFn: () => api.users.list().then(r => r.data.data ?? r.data ?? []),
  });
  const users: any[] = usersData ?? [];

  const [formData, setFormData] = useState(() => {
    const base = {
      jobId: '',
      // No hardcoded default — the template list is the tenant's, not ours.
      template: '',
      title: '',
      // "Prepared by" and "Date" used to sit here, shown and never sent: the
      // JSA table has no column for either. The author is created_by, set from
      // the session, and the date is created_at.
      siteSpecificHazards: [] as string[],
      emergencyProcedures: '',
      weatherConsiderations: '',
      // The picker's value is the supervisor's user id, which is what the API
      // stores (api#811). The name is kept beside it only to label the
      // supervisor's signature; empty id means nobody was named.
      reviewedById: '',
      reviewedBy: '',
      supervisorSignatureKey: '',
      crewSignatures: {} as Record<string, string>,
      steps: defaultJsaSteps(auth?.hasVertical).map((s, i) => ({ id: i + 1, ...s, hazards: [...s.hazards], controls: [...s.controls], ppe: [...s.ppe] })) as Step[],
    };
    if (!existing) return base;
    const loaded = jsaToFormState(existing);
    return {
      ...base,
      jobId: (existing.job_id ?? existing.jobId ?? '__general__') as string,
      title: loaded.title,
      reviewedById: loaded.reviewedById,
      reviewedBy: loaded.reviewedBy,
      supervisorSignatureKey: loaded.supervisorSignatureKey,
      crewSignatures: loaded.crewSignatures,
      steps: loaded.steps.map((s, i) => ({ id: i + 1, ...s })),
      siteSpecificHazards: loaded.siteSpecificHazards,
      emergencyProcedures: loaded.emergencyProcedures,
      weatherConsiderations: loaded.weatherConsiderations,
    };
  });

  // Crew = users minus whoever is the reviewer
  const crewMembers = users.filter(u => u.role !== 'super_admin');
  const supervisors = users.filter(u => ['admin', 'manager', 'supervisor'].includes(u.role));
  // A stored supervisor who is not on the picker's list (an office_staff
  // named on an older JSA) still has to be the selected option, or the select
  // shows the first option while the state keeps the real value.
  const supervisorOptions: { id: string; name: string }[] = supervisors.map((u: any) => ({ id: u.id, name: userName(u) }));
  if (formData.reviewedById && !supervisorOptions.some(o => o.id === formData.reviewedById)) {
    supervisorOptions.push({ id: formData.reviewedById, name: formData.reviewedBy || '—' });
  }

  const toggleStep = (index: number) => {
    if (expandedSteps.includes(index)) {
      setExpandedSteps(expandedSteps.filter(i => i !== index));
    } else {
      setExpandedSteps([...expandedSteps, index]);
    }
  };

  const addStep = () => {
    const newId = Math.max(0, ...formData.steps.map(s => s.id)) + 1;
    setFormData({
      ...formData,
      steps: [...formData.steps, { id: newId, title: '', hazards: [''], controls: [''], ppe: [] }],
    });
    setExpandedSteps([...expandedSteps, formData.steps.length]);
  };

  const removeStep = (index: number) => {
    const updated = formData.steps.filter((_, i) => i !== index);
    setFormData({ ...formData, steps: updated });
    setExpandedSteps(expandedSteps.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  // A new step object every time, never an edit in place: the edit compares
  // these steps against the ones it loaded, and a mutated step would change
  // both sides of that comparison at once.
  const updateStep = (index: number, patch: Partial<Step>) => {
    setFormData({ ...formData, steps: formData.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
  };

  const [error, setError] = useState('');

  const save = (intent: JsaSaveIntent) => {
    if (!isEdit && !formData.jobId) {
      setError('Please select a job or choose "General JSA".');
      return;
    }
    setError('');
    onSubmit(formData, intent);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    save('submit');
  };

  // Edit only: what saving as it stands would do to the signatures on file.
  const edit = isEdit ? buildJsaUpdate(existing, formData) : null;
  const storedSigned: any[] = isEdit
    ? ((existing.crew_signatures ?? existing.crewSignatures ?? []) as any[]).filter(s => jsaSignatureKey(s))
    : [];
  const willClear = (s: any) =>
    !!edit && (edit.contentChanged || (s.role === 'supervisor' && edit.supervisorChanged));
  const clearWarning = (() => {
    if (!edit || edit.cleared === 0) return null;
    const n = edit.cleared;
    const noun = `${n} signature${n === 1 ? '' : 's'}`;
    if (edit.contentChanged) {
      const parts = edit.changedParts.map(p => JSA_SIGNED_PART_LABELS[p]);
      const what = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
      return `Changing ${what} clears ${noun} — the crew will need to re-sign.`;
    }
    return 'Changing the supervisor clears their signature — the new supervisor will need to sign.';
  })();

  function userName(u: any) {
    return `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim();
  }

  // Crew signature picker state — starts empty, crew added explicitly
  const [selectedCrewIds, setSelectedCrewIds] = useState<string[]>([]);

  const addCrewMember = (userId: string) => {
    if (!userId || selectedCrewIds.includes(userId)) return;
    setSelectedCrewIds((prev) => [...prev, userId]);
  };

  const removeCrewMember = (userId: string) => {
    setSelectedCrewIds((prev) => prev.filter((id) => id !== userId));
    const member = crewMembers.find((u: any) => u.id === userId);
    if (member) {
      const name = userName(member);
      // Only a signature captured on this screen is dropped with the card; a
      // stored one stays under the stored-signatures list above.
      const storedKey = jsaToFormState(existing).crewSignatures[name];
      const updated = { ...formData.crewSignatures };
      if (storedKey) updated[name] = storedKey;
      else delete updated[name];
      setFormData({ ...formData, crewSignatures: updated });
    }
  };

  // Users available to pick (not already selected)
  const availableCrew = crewMembers.filter((u: any) => !selectedCrewIds.includes(u.id));

  const submitLabel = isEdit
    ? ((existing.status === 'rejected') ? 'Save & Resubmit' : 'Save & Submit for Approval')
    : 'Submit for Approval';

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pb-4">
      {/* Header Info */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3">Job Safety Analysis</h3>
        <div className="space-y-3">
          {isEdit ? (
            <>
              <div>
                <p className="block text-sm text-fieldiq-text mb-1">Job</p>
                {/* Read-only on purpose: PUT /safety/jsa/:id ignores jobId and
                    still answers 200, so a picker here would look like it saved. */}
                <p className="text-sm text-white" data-testid="jsa-edit-job">{jobLabel(existing)}</p>
                <p className="text-xs text-fieldiq-muted mt-1">The job can't be changed on an existing JSA.</p>
              </div>
              <div>
                <label htmlFor="jsa-title" className="block text-sm text-fieldiq-text mb-1">Title</label>
                <input
                  id="jsa-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={inputClass}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Job <span className="text-fieldiq-muted">(optional)</span></label>
                <select
                  value={formData.jobId}
                  onChange={(e) => setFormData({ ...formData, jobId: e.target.value })}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white"
                >
                  <option value="">— Select an option —</option>
                  <option value="__general__">General JSA (no specific job)</option>
                  {jobs.map((j: any) => (
                    <option key={j.id} value={j.id}>
                      {j.job_number || j.jobNumber} - {j.customer_name || j.customerName || j.well_name || 'Untitled'}
                    </option>
                  ))}
                  {ptItems.map((item) => (
                    <option key={workItemValue(item)} value={workItemValue(item)}>
                      {workItemLabel(item)}
                    </option>
                  ))}
                </select>
                {hasPt && myWork && ptItems.length === 0 && jobs.length === 0 && (
                  <p className="text-fieldiq-muted text-xs mt-1" data-testid="jsa-no-work">
                    You have no open pressure-testing jobs. Choose General JSA to file one without a job.
                  </p>
                )}
                {error && <p className="text-fieldiq-text text-xs mt-1">{error}</p>}
              </div>
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Template</label>
                <select
                  value={formData.template}
                  onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                  disabled={templatesLoading || templates.length === 0}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white disabled:opacity-60"
                >
                  <option value="">
                    {templatesLoading
                      ? 'Loading templates…'
                      : templates.length === 0
                        ? 'No templates configured'
                        : '— Select a template —'}
                  </option>
                  {templates.map((t: any) => (
                    <option key={t.id ?? templateName(t)} value={templateName(t)}>{templateName(t)}</option>
                  ))}
                </select>
                {/* An empty list is a real answer, not a spinner that never ends —
                  * say so, rather than showing a select the reader cannot open and
                  * leaving them to guess whether it is still loading. */}
                {!templatesLoading && templates.length === 0 && (
                  <p className="text-xs text-fieldiq-muted mt-1">
                    No JSA templates set up for this company yet. The analysis saves without one.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Job Steps */}
      <div className="space-y-3">
        {formData.steps.map((step, index) => (
          <div key={step.id} className="bg-fieldiq-card rounded-xl overflow-hidden">
            <div className="flex items-center bg-fieldiq-dark">
              <button
                type="button"
                onClick={() => toggleStep(index)}
                aria-expanded={expandedSteps.includes(index)}
                className="flex-1 px-4 py-3 flex justify-between items-center text-left"
              >
                <span className="font-semibold">Step {index + 1}: {step.title || '(Untitled)'}</span>
                {expandedSteps.includes(index) ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {formData.steps.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  aria-label={`Remove step ${index + 1}`}
                  className="text-fieldiq-muted hover:text-fieldiq-text transition-colors p-3"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            {expandedSteps.includes(index) && (
              <div className="p-4 space-y-4">
                <div>
                  <label htmlFor={`jsa-step-${step.id}-title`} className="block text-xs text-fieldiq-text mb-1">Step Title</label>
                  <input
                    id={`jsa-step-${step.id}-title`}
                    value={step.title}
                    onChange={(e) => updateStep(index, { title: e.target.value })}
                    className={inputClass}
                    placeholder="Step name..."
                  />
                </div>

                <div>
                  <h4 className="text-sm font-medium text-fieldiq-text mb-2">Hazards</h4>
                  <ListEditor
                    label={`Step ${index + 1} hazard`}
                    items={step.hazards}
                    onChange={(hazards) => updateStep(index, { hazards })}
                    addLabel="Add hazard"
                  />
                </div>

                <div>
                  <h4 className="text-sm font-medium text-fieldiq-text mb-2">Control Measures</h4>
                  <ListEditor
                    label={`Step ${index + 1} control`}
                    items={step.controls}
                    onChange={(controls) => updateStep(index, { controls })}
                    addLabel="Add control"
                  />
                </div>

                <div>
                  <h4 className="text-sm font-medium text-fieldiq-text mb-2">PPE Required</h4>
                  <div className="flex flex-wrap items-center gap-2">
                    {step.ppe.map((item) => (
                      <span key={item} className="inline-flex items-center gap-1 bg-fieldiq-dark px-2 py-1 rounded text-xs">
                        {ppeIcons[item] || <HardHat size={16} />}
                        {item}
                        <button
                          type="button"
                          onClick={() => updateStep(index, { ppe: step.ppe.filter(p => p !== item) })}
                          aria-label={`Remove ${item} from step ${index + 1}`}
                          className="ml-0.5 text-fieldiq-muted hover:text-fieldiq-text"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                    {PPE_OPTIONS.some(p => !step.ppe.includes(p)) && (
                      <select
                        value=""
                        onChange={(e) => { if (e.target.value) updateStep(index, { ppe: [...step.ppe, e.target.value] }); }}
                        aria-label={`Add PPE to step ${index + 1}`}
                        className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1 text-xs text-white"
                      >
                        <option value="">+ Add PPE</option>
                        {PPE_OPTIONS.filter(p => !step.ppe.includes(p)).map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={addStep}
          className="w-full border-2 border-dashed border-fieldiq-border rounded-xl p-4 text-center text-fieldiq-text hover:border-fieldiq-accent transition"
        >
          <Plus size={20} className="mx-auto mb-1" />
          Add Custom Step
        </button>
      </div>

      {/* Site conditions. Part of what the crew signs, like the steps: a change
          here clears the signatures on file (api#815), and the warning says so. */}
      <div className="bg-fieldiq-card rounded-xl p-4 space-y-4">
        <h3 className="font-semibold">Site conditions</h3>
        <div>
          <h4 className="text-sm font-medium text-fieldiq-text mb-2">Site-Specific Hazards</h4>
          <ListEditor
            label="Site hazard"
            items={formData.siteSpecificHazards}
            onChange={(siteSpecificHazards) => setFormData({ ...formData, siteSpecificHazards })}
            addLabel="Add site hazard"
          />
        </div>
        <div>
          <label htmlFor="jsa-emergency-procedures" className="block text-sm font-medium text-fieldiq-text mb-1">Emergency Procedures</label>
          <textarea
            id="jsa-emergency-procedures"
            rows={3}
            value={formData.emergencyProcedures}
            onChange={(e) => setFormData({ ...formData, emergencyProcedures: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="jsa-weather-considerations" className="block text-sm font-medium text-fieldiq-text mb-1">Weather Considerations</label>
          <textarea
            id="jsa-weather-considerations"
            rows={2}
            value={formData.weatherConsiderations}
            onChange={(e) => setFormData({ ...formData, weatherConsiderations: e.target.value })}
            className={inputClass}
          />
        </div>
      </div>

      {/* Signatures */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3">Signatures</h3>
        <div className="space-y-3">
          <div>
            <label htmlFor="jsa-supervisor" className="block text-sm text-fieldiq-text mb-1">Reviewed by (Supervisor)</label>
            <select
              id="jsa-supervisor"
              value={formData.reviewedById}
              onChange={(e) => {
                const chosen = supervisorOptions.find(o => o.id === e.target.value);
                setFormData({ ...formData, reviewedById: chosen ? chosen.id : '', reviewedBy: chosen ? chosen.name : '' });
              }}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white"
            >
              <option value="">Select Supervisor</option>
              {supervisorOptions.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Signatures already on the JSA (edit only) */}
      {isEdit && (
        <div className="bg-fieldiq-card rounded-xl p-4" data-testid="jsa-stored-signatures">
          <h3 className="font-semibold mb-3">Signatures on file</h3>
          {storedSigned.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">Nobody has signed this JSA yet.</p>
          ) : (
            <ul className="space-y-2">
              {storedSigned.map((s, i) => (
                <li key={i} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-white">
                    {s.name || '—'}
                    <span className="text-xs text-fieldiq-muted ml-2">
                      {s.role === 'supervisor' ? 'Supervisor' : 'Crew'} · signed {formatInstantDay(s.signedAt ?? s.signed_at, timeZone) ?? '—'}
                    </span>
                  </span>
                  <span className="text-xs font-medium text-fieldiq-text">
                    {willClear(s) ? 'Will be cleared' : 'Kept'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Supervisor Signature */}
      <SignatureCapture
        formName="jsa"
        submissionId={submissionId}
        label="Supervisor Signature"
        // Functional updates, in every signature callback: an upload resolves
        // after the render that started it, so a spread of the `formData` it
        // closed over drops whatever landed meanwhile. Two signatures confirmed
        // together kept only the second, silently.
        onSigned={(key) => setFormData(prev => ({ ...prev, supervisorSignatureKey: key }))}
        onCleared={() => setFormData(prev => ({ ...prev, supervisorSignatureKey: isEdit ? jsaToFormState(existing).supervisorSignatureKey : '' }))}
      />

      {/* Crew Signatures */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h3 className="font-semibold text-white">{isEdit ? 'Sign or re-sign' : 'Crew Signatures'}</h3>
          {availableCrew.length > 0 && (
            <select
              value=""
              onChange={(e) => { addCrewMember(e.target.value); e.target.value = ''; }}
              className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-fieldiq-accent-signal"
            >
              <option value="">+ Add Crew Member</option>
              {availableCrew.map((u: any) => (
                <option key={u.id} value={u.id}>{userName(u)}</option>
              ))}
            </select>
          )}
        </div>

        {selectedCrewIds.length === 0 ? (
          <div className="bg-fieldiq-card rounded-xl p-4 text-center text-fieldiq-muted text-sm">
            No crew members added yet. Use "Add Crew Member" above to begin.
          </div>
        ) : (
          selectedCrewIds.map((userId) => {
            const member = crewMembers.find((u: any) => u.id === userId);
            if (!member) return null;
            const name = userName(member);
            return (
              <div key={member.id} className="bg-fieldiq-card rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-fieldiq-accent flex items-center justify-center text-fieldiq-dark font-bold text-sm">
                    {name.split(' ').map((n: string) => n[0]).join('')}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-white">{name}</span>
                    <span className="text-xs text-fieldiq-muted ml-2 capitalize">{(member.role || '').replace(/_/g, ' ')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCrewMember(userId)}
                    aria-label={`Remove ${name}`}
                    className="text-fieldiq-muted hover:text-fieldiq-text p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <SignatureCapture
                  formName="jsa"
                  submissionId={submissionId}
                  label={`${name}'s signature`}
                  onSigned={(key) =>
                    setFormData(prev => ({
                      ...prev,
                      crewSignatures: { ...prev.crewSignatures, [name]: key },
                    }))
                  }
                  onCleared={() => {
                    const storedKey = isEdit ? jsaToFormState(existing).crewSignatures[name] : undefined;
                    setFormData(prev => {
                      const updated = { ...prev.crewSignatures };
                      if (storedKey) updated[name] = storedKey;
                      else delete updated[name];
                      return { ...prev, crewSignatures: updated };
                    });
                  }}
                />
              </div>
            );
          })
        )}
      </div>

      {clearWarning && (
        <div role="status" data-testid="jsa-signature-clear-warning" className="flex items-start gap-2 rounded-lg border border-fieldiq-border bg-fieldiq-card p-3 text-sm text-fieldiq-text">
          <AlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-fieldiq-accent-signal" />
          <span>{clearWarning}</span>
        </div>
      )}

      {/* Actions */}
      <div className="border-t border-fieldiq-border px-4 pt-4 pb-[var(--bottom-chrome)] flex gap-3 mt-4">
        <button
          type="button"
          onClick={() => save('save')}
          disabled={saving}
          className="flex-1 bg-fieldiq-border text-white py-3 rounded-lg font-medium disabled:opacity-50"
        >
          {isEdit ? 'Save changes' : 'Save Draft'}
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-3 rounded-lg font-medium disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
